/* eslint-disable no-nested-ternary */
import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { GridApi, GridOptions, GridReadyEvent } from 'ag-grid-enterprise'
import { useSelector } from 'react-redux'
import { Button, Segmented, Tooltip } from 'antd'
import Icon, { PercentageOutlined, EditOutlined, AppstoreOutlined, DownloadOutlined } from '@ant-design/icons'
import { Sigma } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { StatementColumn } from '../../types'
import CustomAgGrid from '../../../../shared/components/custom/Grid/CustomAgGrid'
import { EnhancedStatementRow } from '../../utils/dataEnhancement'
import { filtersSelector } from '../../../../redux/context/filters/selectors'
import { createExcelStyles } from '../../../../shared/utils/excelStyles'
import { useFinancialStatementsState } from '../../hooks/useFinancialStatementsState'
import { useIncomeStatementColumns } from '../../hooks/useIncomeStatementColumns'
import { useStatementContextMenu } from '../../hooks/useStatementContextMenu'
import { useRowFiltering } from '../../hooks/useRowFiltering'
import { useDimensionLoading } from '../../hooks/useDimensionLoading'
import { useAsyncGridData } from '../../hooks/useAsyncGridData'
import { useTreeDataTransaction } from '../../hooks/useTreeDataTransaction'
import { useCtxReportablePeriodGroups } from '../../../fiscalYear/hooks/useCtxReportablePeriodGroups'
import { getCellSelectionConfig, getStatusBarConfig, getColumnsSideBarConfig, getRowClass } from './utils/gridConfig'
import { GridToolbar } from '../../../../shared/components/GridToolbar'
import { NumberFormatControls } from '../../../../shared/components/NumberFormatControls'
import { shouldUseDualValueDisplay } from '../../utils/columnUtils'
import { useGridContext } from '../../../../shared/hooks/useGridContext'
import { useBudgetDisplayMode, BudgetDisplayMode } from '../../hooks/useBudgetDisplayMode'
import { convertToAccountBudgetData } from '../../../account/utils/convertToAccountBudgetData'
import { useUpdateAccountBudgets } from '../../../account/queries/useUpdateAccountBudgets'
import { useBudgetAllocation } from '../../../budgetAllocation'
import useUnsavedChangesBlocker from '../../../../shared/hooks/useUnsavedChangesBlocker'
import { useGridDataActions } from '../../../../shared/hooks/useGridDataActions'
import { useExcelExport } from '../../hooks/useExcelExport'
import { setRegistryValue } from '../../../../shared/utils/balanceRegistry'
import { DisplayScale, NumberFormatConfig } from '../../../../shared/utils/numberScale'
import './styles.css'

interface IncomeStatementTableProps {
  treeData: EnhancedStatementRow[]
  columns: StatementColumn[]
  loading?: boolean
  isBudgetingPage?: boolean
  companyId: string
  financialStatementsFilters: ReturnType<typeof useFinancialStatementsState>['financialStatementsFilters']
  dimensionMap: ReturnType<typeof useFinancialStatementsState>['dimensionMap']
  dimensionList: ReturnType<typeof useFinancialStatementsState>['dimensionList']
  getDimensionParams: ReturnType<typeof useFinancialStatementsState>['getDimensionParams']
  hideControls?: boolean
  displayScale?: DisplayScale
  onDisplayScaleChange?: (scale: DisplayScale) => void
  decimals?: number
  onDecimalsChange?: (decimals: number) => void
}

const noop = () => undefined

const IncomeStatementTable: React.FC<IncomeStatementTableProps> = ({
  treeData,
  columns,
  loading = false,
  isBudgetingPage = false,
  companyId,
  financialStatementsFilters,
  dimensionMap,
  dimensionList,
  getDimensionParams,
  hideControls = false,
  displayScale = DisplayScale.default,
  onDisplayScaleChange = noop,
  decimals = 0,
  onDecimalsChange = noop
}) => {
  const { t } = useTranslation()
  const gridApiRef = useRef<GridApi<EnhancedStatementRow>>()
  const [isGridReady, setIsGridReady] = useState(false)
  const [showPercentage, setShowPercentage] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [shouldExitEditMode, setShouldExitEditMode] = useState(false)
  const { dimensions, budgetingScenario } = useSelector(filtersSelector)
  const { mutateAsync: saveAccountBudgets } = useUpdateAccountBudgets()
  const { displayEmptyRows, dataTypes } = useSelector(filtersSelector)
  const isScenarioLocked = budgetingScenario?.isLocked === true

  // Budget display mode (auto-switches mode when entering edit mode)
  const { showCalculated, showInput, displayMode, setDisplayMode } = useBudgetDisplayMode({
    isEditMode,
    hasSubDimensions: dimensionMap?.has(dimensions?.[0] ?? null),
    isBudgetingPage
  })

  const { hasUnsavedChanges, changedRowsCount, discardChanges } = useGridDataActions<EnhancedStatementRow>({
    gridApi: gridApiRef.current
  })

  // Force edit mode off if not on budgeting page or scenario becomes locked
  useEffect(() => {
    if (isEditMode && (!isBudgetingPage || isScenarioLocked)) {
      setIsEditMode(false)
      discardChanges()
    }
  }, [isBudgetingPage, isScenarioLocked, isEditMode, discardChanges])

  // Budget allocation hook - handles modal and save flow
  const { AllocationModal, collectChangedRows, hasGroupChanges, setIsModalOpen } = useBudgetAllocation({
    gridApi: gridApiRef.current,
    columns,
    entityType: 'account',
    onSave: async (budgetData: any) => {
      // Wait for save + refetch to complete (financial statements are now loaded)
      await saveAccountBudgets(budgetData)
      // Mark that we should exit edit mode when dimension loading completes
      setShouldExitEditMode(true)
    }
  })

  // Block navigation when there are unsaved changes
  useUnsavedChangesBlocker({
    hasUnsavedChanges,
    onDiscard: discardChanges
  })

  const handleSave = async () => {
    const changedRows = collectChangedRows()

    if (changedRows.length === 0) {
      console.log('No changes to save')
      return
    }

    if (hasGroupChanges(changedRows)) {
      // Group data → open modal for allocation
      setIsModalOpen(true)
      return // Keep edit mode active
    }

    // Only elements data → direct save
    const budgetData = convertToAccountBudgetData({ rowData: changedRows })
    // Wait for save + refetch to complete (financial statements are now loaded)
    await saveAccountBudgets(budgetData)
    // Mark that we should exit edit mode when dimension loading completes
    setShouldExitEditMode(true)
  }

  // Update grid data using transactions to preserve expanded state and async-loaded rows
  // Also fetches and updates dimension data automatically when parent rows update
  const { isDimensionLoading } = useTreeDataTransaction(
    gridApiRef.current,
    treeData,
    loading,
    companyId,
    financialStatementsFilters.budgetingScenarioId,
    financialStatementsFilters.dimensions,
    getDimensionParams,
    dimensionMap,
    dimensionList
  )

  const onDimensionRowGroupOpened = useDimensionLoading(
    gridApiRef.current,
    getDimensionParams,
    dimensionMap,
    dimensionList
  )

  const onAsyncRowGroupOpened = useAsyncGridData(
    gridApiRef.current,
    companyId,
    financialStatementsFilters.budgetingScenarioId,
    financialStatementsFilters.periodGroups
  )

  // Combined row group opened handler
  const onRowGroupOpened = useCallback(
    (event: any) => {
      onDimensionRowGroupOpened(event)
      // Handle async data loading (investment, amendment, counter-amendment)
      onAsyncRowGroupOpened(event)
    },
    [onDimensionRowGroupOpened, onAsyncRowGroupOpened]
  )

  // Column definitions
  const { columnDefs, autoGroupColumnDef } = useIncomeStatementColumns({
    columns,
    dataTypes
  })

  // Grid configuration
  // No scale suffix (k/M) in cells — the current scale is already shown by the toolbar toggle above the table.
  const numberFormat = useMemo<NumberFormatConfig>(
    () => ({ decimals, displayScale, showScaleSuffix: false }),
    [decimals, displayScale]
  )
  const excelStyles = useMemo(() => createExcelStyles(), [])
  const cellSelection = useMemo(() => getCellSelectionConfig(), [])
  const statusBar = useMemo(() => getStatusBarConfig(), [])
  const sideBar = useMemo(() => getColumnsSideBarConfig(), [])
  const gridContext = useGridContext({
    gridApi: gridApiRef.current,
    isEditMode,
    showCalculated,
    showInput,
    showPercentage,
    numberFormat
  })
  const onExcelExport = useExcelExport(gridApiRef.current, 'incomeStatement', gridContext)

  // Get fiscal years/period groups
  const periodGroups = useCtxReportablePeriodGroups()

  // Custom hooks
  const getContextMenuItems = useStatementContextMenu()
  const { isExternalFilterPresent, doesExternalFilterPass } = useRowFiltering(displayEmptyRows, columns, periodGroups)

  // Grid options
  const gridOptions = useMemo(
    (): GridOptions<EnhancedStatementRow> => ({
      defaultColDef: {
        resizable: true,
        rowDrag: false,
        sortable: false,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        enableCellChangeFlash: true
      },
      stopEditingWhenCellsLoseFocus: true,
      includeHiddenColumnsInQuickFilter: true,
      hidePaddedHeaderRows: true,
      cellSelection: true,
      treeData: true,
      excludeChildrenWhenTreeDataFiltering: true,
      getDataPath: row => row.path,
      getRowId: params => params.data.key,
      getContextMenuItems,
      getRowHeight: params => {
        if (shouldUseDualValueDisplay(params)) {
          // For dual value display, use taller rows to accommodate both values
          return params.context.showPercentage ? 72 : 48
        }
        // Single value display
        return params.context.showPercentage ? 40 : 24
      },
      getRowClass
    }),
    []
  )

  // Recalculate row heights when display mode or percentage view changes
  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.resetRowHeights()
    }
  }, [showPercentage, displayMode])

  // Exit edit mode after save when both main data and dimension data are loaded
  // We need to wait for both loading states to complete to ensure all data is up-to-date
  useEffect(() => {
    if (shouldExitEditMode && !loading && !isDimensionLoading) {
      setIsEditMode(false)
      discardChanges()
      setShouldExitEditMode(false)
    }
  }, [shouldExitEditMode, loading, isDimensionLoading, discardChanges])

  // Event handlers
  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api
    setIsGridReady(true)
    // Data is managed via transactions in useTreeDataTransaction, not here
  }, [])

  // Handle cell editing stopped - force value to be stored even if unchanged
  const onCellEditingStopped = useCallback((event: any) => {
    // If AG Grid says value didn't change, we still want to mark it as modified
    if (!event.valueChanged && event.newValue !== undefined && event.data && event.column) {
      const columnDef = event.column.getColDef()
      const columnData = columnDef.headerComponentParams?.columnData

      if (columnData && columnData.dataKey !== undefined) {
        // Force storage using setRegistryValue directly
        setRegistryValue(event.data, columnData, event.newValue, 'newBudgets')
        // Refresh the cell to show it as modified
        event.api.refreshCells({ rowNodes: [event.node], force: true })
      }
    }
  }, [])

  return (
    <>
      {!hideControls && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
            {t('financialStatementsPage:incomeStatement')}
          </h2>
        </div>
      )}
      {!hideControls && (
        <GridToolbar>
          <GridToolbar.Actions align="space-between">
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {isBudgetingPage && (
                <Tooltip title={t('financialStatementsPage:displayModeTooltip')}>
                  <Segmented<BudgetDisplayMode>
                    className="segmented-no-animation"
                    value={displayMode}
                    onChange={val => setDisplayMode(val)}
                    options={[
                      {
                        label: t('financialStatementsPage:calculated'),
                        value: 'calculated',
                        icon: <Icon component={() => <Sigma size={14} />} />
                      },
                      {
                        label: t('financialStatementsPage:input'),
                        value: 'input',
                        icon: <EditOutlined />
                      },
                      {
                        label: t('financialStatementsPage:both'),
                        value: 'dual-value',
                        icon: <AppstoreOutlined />
                      }
                    ]}
                  />
                </Tooltip>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <NumberFormatControls
                displayScale={displayScale}
                onDisplayScaleChange={onDisplayScaleChange}
                decimals={decimals}
                onDecimalsChange={onDecimalsChange}
                disabled={isEditMode}
              />
              <Button
                type={showPercentage ? 'primary' : 'default'}
                icon={<PercentageOutlined />}
                onClick={() => setShowPercentage(!showPercentage)}
                disabled={isEditMode}
              />
              <GridToolbar.Actions>
                <Button onClick={onExcelExport} icon={<DownloadOutlined />}>
                  {t('financialStatementsPage:excel')}
                </Button>
              </GridToolbar.Actions>
              {isBudgetingPage && (
                <GridToolbar.EditModeActions
                  gridApi={isGridReady ? gridApiRef.current : undefined}
                  isEditMode={isEditMode}
                  onEnterEditMode={() => setIsEditMode(true)}
                  onExitEditMode={() => setIsEditMode(false)}
                  changedRowsCount={changedRowsCount}
                  onDiscard={discardChanges}
                  onSave={handleSave}
                  primaryEdit
                  disabled={isScenarioLocked}
                  disabledTooltip={t('global:isLocked')}
                />
              )}
            </div>
          </GridToolbar.Actions>
        </GridToolbar>
      )}

      <div style={{ flex: 1 }}>
        <CustomAgGrid
          {...gridOptions}
          rowData={undefined} // Data managed via transactions in useTreeDataTransaction
          loading={loading || isDimensionLoading}
          autoGroupColumnDef={autoGroupColumnDef}
          context={gridContext}
          columnDefs={columnDefs}
          excelStyles={excelStyles}
          statusBar={statusBar}
          cellSelection={cellSelection}
          sideBar={sideBar}
          undoRedoCellEditing
          undoRedoCellEditingLimit={20}
          onGridReady={onGridReady}
          onRowGroupOpened={onRowGroupOpened}
          onCellEditingStopped={onCellEditingStopped}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
        />
      </div>

      {/* Budget allocation modal */}
      {AllocationModal}
    </>
  )
}

export default IncomeStatementTable
